import { PlusIcon } from '@heroicons/react/24/solid'
import Button from 'components/Button'
import FormRow from 'components/Form/FormRow'
import { Combobox } from 'components/Form/Inputs/Combobox'
import InputField from 'components/Form/Inputs/InputField/component'
import SelectField from 'components/Form/Inputs/SelectField'
import ModalBody from 'components/Modal/ModalBody'
import ModalFooter from 'components/Modal/ModalFooter'
import UrlModal from 'components/Modal/UrlModal'
import { useGetCategoriesQuery } from 'containers/categories/queries'
import { useGetItemsQuery } from 'containers/items/queries'
import { useQuantityUnitsQuery } from 'containers/quantityUnits/queries'
import { CurrentIngredients } from 'containers/recipes/forms/editRecipeIngredients/currentIngredients'
import { useAddItemToRecipeMutation, useGetSingleRecipeQuery } from 'containers/recipes/queries'
import { AddItemToRecipePayload } from 'containers/recipes/types'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

export function EditRecipeIngredients() {
    const navigate = useNavigate()
    const { recipeId } = useParams()

    const [addIngredientName, setAddIngredientName] = useState('')
    const [newItemCategory, setNewItemCategory] = useState('')
    const [quantityValueToAdd, setQuantityValueToAdd] = useState('1')
    const [quantityUnitToAdd, setQuantityUnitToAdd] = useState('NO_UNIT')

    const { data: singleRecipeData } = useGetSingleRecipeQuery(recipeId ?? '')
    const { data: quantityUnitsData, isLoading: isQuantityUnitsPending } = useQuantityUnitsQuery()
    const { data: itemsData, isLoading: isItemsPending } = useGetItemsQuery()
    const { data: itemCategoriesData, isLoading: isItemCategoriesPending } = useGetCategoriesQuery()
    const { mutate: addItemToRecipe } = useAddItemToRecipeMutation()

    if (!singleRecipeData || !quantityUnitsData || !itemsData || !itemCategoriesData) {
        return null
    }

    const quantityUnitOptions = [
        {
            label: 'no unit',
            value: 'NO_UNIT'
        },
        ...(quantityUnitsData?.map((quantityUnit) => ({
            label: quantityUnit.symbol,
            value: quantityUnit.id.toString()
        })) || [])
    ]

    const isNewItem = !!addIngredientName && !itemsData.find(({ name }) => name === addIngredientName)

    function addItem() {
        if (!addIngredientName) {
            return
        }

        const payload: AddItemToRecipePayload = { recipeId: recipeId ?? '', itemName: addIngredientName, quantity: Number(quantityValueToAdd) }

        if (isNewItem) {
            const matchingExistingCategoryId = itemCategoriesData?.find((category) => category.name === newItemCategory)?.id

            if (matchingExistingCategoryId) {
                payload.existingCategoryId = matchingExistingCategoryId.toString()
            } else {
                payload.newCategory = newItemCategory
            }
        }

        if (quantityUnitToAdd && quantityUnitToAdd !== 'NO_UNIT') {
            payload.quantityUnitId = Number(quantityUnitToAdd)
        }

        addItemToRecipe(payload)

        setAddIngredientName('')
        setNewItemCategory('')
        setQuantityValueToAdd('1')
        setQuantityUnitToAdd('NO_UNIT')
    }

    return (
        <UrlModal
            title='Edit Recipe'
            desc='Ingredients'
            onClose={() => navigate(-1)}
            loading={isItemsPending || isItemCategoriesPending || isQuantityUnitsPending}
        >
            <>
                <ModalBody>
                    <div className='min-h-64'>
                        {isNewItem && (
                            <FormRow>
                                <Combobox
                                    label='Choose Ingredient Category'
                                    value={newItemCategory}
                                    setValue={setNewItemCategory}
                                    options={itemCategoriesData.map((category) => {
                                        return {
                                            id: category.id,
                                            value: category.name
                                        }
                                    })}
                                />
                            </FormRow>
                        )}

                        <div>
                            <p>Add Ingredient</p>
                            <div className='flex gap-1 mb-4'>
                                <InputField
                                    name='quanity'
                                    onChange={(e) => setQuantityValueToAdd(e.target.value)}
                                    value={quantityValueToAdd}
                                    type='number'
                                    className='px-1 w-12 sm:w-20'
                                />
                                <SelectField
                                    name='quantityUnit'
                                    onChange={(e) => setQuantityUnitToAdd(e.target.value)}
                                    value={quantityUnitToAdd}
                                    options={quantityUnitOptions}
                                    className='px-[2px] w-[85px]'
                                />
                                <Combobox
                                    value={addIngredientName}
                                    setValue={setAddIngredientName}
                                    options={itemsData.map(({ name, id }) => ({ value: name, id }))}
                                    onEnterKey={addItem}
                                    isFullWidth
                                />
                                <div className='w-10 h-9 flex justify-center items-center relative'>
                                    <button onClick={addItem}>
                                        <PlusIcon className='w-8 text-primary hover:text-primary-hover' />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <CurrentIngredients />
                    </div>
                </ModalBody>
                <ModalFooter
                    buttons={[
                        <Button key={1} onClick={() => navigate(-1)}>
                            Done
                        </Button>
                    ]}
                />
            </>
        </UrlModal>
    )
}
